import { useEffect, useRef, useState } from 'react';
import type { AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import { canEditTodos, canEnterEdit, canViewTodos } from '../../lib/permissions';
import type { MapNode, ProjectPlan } from '../../lib/types';
import { projectText } from '../../i18n/projects';
import { DatePicker } from '../../components/DatePicker';
import { formatDue, todayKey } from './TodoSection';

/**
 * THE PROJECT MARK IN THE POPUP'S HEAD (V0.50.3) — the seat, the star, the bell, the address
 * button, then THIS: a flag button that says whether the node is a project, and when it is,
 * its two dates to the right and a way to the Projects tab. The user asked for the mark to
 * move up here from the To-dos section (V0.50.2), beside the node's name, so that what a node
 * IS reads in one line before what it carries.
 *
 * THE VOCABULARY IS THE HEAD'S OWN: the button is a `.favbtn` like the three before it (lit in
 * accent when on, the address button's colour), the dates are the To-dos editor's compact date
 * fields, the Progress button is a fourth `.favbtn` carrying the dashboard's chart glyph.
 *
 * WHO SEES WHAT. The mark and its dates ride the map payload (`MapNode.project`) for EVERY
 * reader — a statement about the node, like the documentation plan — so a project shows a
 * read pill and its dates to anyone. The flag BUTTON draws for a to-do manager
 * (`canEditTodos`, with the V0.50 carve-out: in the read shape only for an account that has no
 * Edit toggle to reach for), and Progress for anyone who may read to-dos, since that is the tab's
 * own gate. A node that is not a project shows nothing to anyone who cannot mark one — which is
 * what keeps `node-popup.png` (the read shape of a standard-doc node, opened by a participant)
 * exactly as it was.
 *
 * COMMITS AT ONCE through `setNodeProject`, which adopts the `dataVersion` the route now returns
 * (the mark rides the map, so marking bumps — see routes/projects.ts rule 2). The dates commit
 * the WHOLE pair on blur, Enter or a clear, never per `change`: Chrome fires `change` for every
 * valid intermediate value while a year is typed, and `0002-09-19` is a valid day. Escape puts
 * the typed value back and stops there — `chrome.ts` closes the popup on Escape.
 */
export function ProjectHead({ node, s, readOnly }: { node: MapNode; s: AppSnapshot; readOnly: boolean }) {
  const project = node.project ?? null;
  const mayEdit = canEditTodos(s.perm) && (!readOnly || !canEnterEdit(s.perm));
  const mayProgress = canViewTodos(s.perm);
  const locale = s.currentLocale;
  const [busy, setBusy] = useState(false);
  /* THE DATES REST AS A PILL AND BECOME FIELDS ON A CLICK (V0.51). Two `input[type=date]`
     sitting open on the seat's line made the head a form: five buttons and two fields on the
     row that is supposed to be the node. They say the same thing in a third of the width
     until somebody wants to change them, and the reading shape below has drawn exactly that
     since V0.50.3 — this borrows it rather than inventing a second way to show a date range. */
  const [editingDates, setEditingDates] = useState(false);
  const [start, setStart] = useState(project?.startDate ?? '');
  const [due, setDue] = useState(project?.dueDate ?? '');
  // What the last commit sent, so a blur after a clear (which committed already) sends nothing twice.
  const lastSent = useRef(JSON.stringify({ startDate: project?.startDate ?? null, dueDate: project?.dueDate ?? null }));
  useEffect(() => {
    setStart(project?.startDate ?? '');
    setDue(project?.dueDate ?? '');
    lastSent.current = JSON.stringify({ startDate: project?.startDate ?? null, dueDate: project?.dueDate ?? null });
  }, [project?.startDate, project?.dueDate]);

  if (!project && !mayEdit) return null;

  const commit = async (draft: ProjectPlan | null) => {
    setBusy(true);
    try {
      await (callAction('setNodeProject', node.id, draft) as Promise<boolean>);
    } finally {
      setBusy(false);
    }
  };
  const send = (draft: ProjectPlan) => {
    const key = JSON.stringify(draft);
    if (key === lastSent.current) return;
    lastSent.current = key;
    void commit(draft);
  };
  /**
   * A PICK IS A WHOLE ANSWER, SO IT COMMITS AT ONCE (V0.51.1).
   *
   * The V0.50.3 rule was "blur commits and nothing else", and it existed because a typed date
   * field emits `change` for every intermediate value while a year is keyed in — Chrome will
   * happily report `0002-09-19` as a valid day on the way to 2026. There is no typing any more:
   * the only thing that can come out of a `DatePicker` is a day somebody chose or a cleared
   * field, so waiting for a blur would just be a save somebody has to remember to trigger.
   *
   * The editor stays revealed until Escape or the popup closes, which is what a disclosure
   * does, and is why setting both dates is still one journey through the pill rather than two.
   */
  const setDates = (next: { startDate: string | null; dueDate: string | null }) => {
    setStart(next.startDate ?? '');
    setDue(next.dueDate ?? '');
    send(next);
  };

  const range = project
    ? project.startDate || project.dueDate
      ? `${project.startDate ? formatDue(project.startDate) : '…'} → ${project.dueDate ? formatDue(project.dueDate) : '…'}`
      : projectText('noDates', locale)
    : '';

  const progress = project && mayProgress ? (
    <button
      type="button"
      className="favbtn pp-project-progress"
      id="pp-project-progress"
      title={projectText('progressTitle', locale)}
      onClick={() => callAction('openProjectProgress', node.id)}
    >
      <svg className="ico">
        <use href="#i-chart" />
      </svg>
    </button>
  ) : null;

  if (!mayEdit) {
    // A reader: the mark as a pill, the dates as text. Drawn only for a project (see the header).
    return (
      <span className="pp-project" id="pp-project" data-node={node.id}>
        <span className="pp-project-pill" title={projectText('isProject', locale)}>
          <svg className="ico">
            <use href="#i-flag" />
          </svg>
          {projectText('badge', locale)}
        </span>
        <span className="pp-head-dates read">{range}</span>
        {progress}
      </span>
    );
  }
  return (
    <span className="pp-project" id="pp-project" data-node={node.id}>
      <button
        type="button"
        className={project ? 'favbtn projbtn on' : 'favbtn projbtn'}
        id="pp-project-on"
        aria-pressed={!!project}
        title={projectText(project ? 'markOn' : 'markOff', locale)}
        disabled={busy}
        onClick={() => {
          lastSent.current = '';
          void commit(project ? null : { startDate: null, dueDate: null });
        }}
      >
        <svg className="ico">
          <use href="#i-flag" />
        </svg>
      </button>
      {project && !editingDates ? (
        <button
          type="button"
          className="pp-datepill"
          id="pp-datepill"
          title={projectText('editDates', locale)}
          disabled={busy}
          onClick={() => setEditingDates(true)}
        >
          {range}
        </button>
      ) : null}
      {project && editingDates ? (
        <span
          className="pp-head-dates"
          /* Escape stands the pair down and must not reach `chrome.ts`, which closes the whole
             popup on it. The calendar swallows its own Escape first, so the first press closes
             the grid and the second folds the dates away. */
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            setStart(project?.startDate ?? '');
            setDue(project?.dueDate ?? '');
            setEditingDates(false);
          }}
        >
          <label className="todoedit-due pp-head-date">
            {projectText('startDate', locale)}
            <DatePicker
              id="pp-project-start"
              value={start || null}
              today={todayKey()}
              max={due || null}
              quick
              disabled={busy}
              locale={locale}
              onChange={(day) => setDates({ startDate: day, dueDate: due || null })}
            />
          </label>
          <span className="pp-head-arrow" aria-hidden="true">
            →
          </span>
          <label className="todoedit-due pp-head-date">
            {projectText('targetDate', locale)}
            <DatePicker
              id="pp-project-due"
              value={due || null}
              today={todayKey()}
              min={start || null}
              quick
              disabled={busy}
              locale={locale}
              onChange={(day) => setDates({ startDate: start || null, dueDate: day })}
            />
          </label>
        </span>
      ) : null}
      {progress}
    </span>
  );
}
